import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  DeliveryAnalyticsFilters,
  PopularAssetsResponse,
  AudienceResponse,
  AudienceDimension,
  TimeseriesResponse,
  CachePerformanceResponse,
  PopularTransformsResponse,
  TransformGroupBy,
  TransformCoverageResponse,
  CampaignsResponse,
  CampaignDetailResponse,
  ChannelsResponse,
  ChannelDetailResponse,
  DistinctValuesResponse,
  DistinctDimension,
} from 'src/types/TDeliveryAnalytics';
import {
  getPopularAssets,
  getAudience,
  getTimeseries,
  getCachePerformance,
  getPopularTransforms,
  getTransformCoverage,
  getCampaigns,
  getCampaignDetail,
  getChannels,
  getChannelDetail,
  getDistinctValues,
} from '../functions/deliveryAnalyticsApi';
import { queryNames } from '../utils/queryNames';

// First paint can race the backend's /stats/* rate limiter (429) or hit a
// briefly-unavailable receiver (5xx). Retry only those transient failures so we
// self-heal without looping on 403 (permission) / 502 endpoint config.
const retryOnTransient = (failureCount: number, error: FetchError): boolean => {
  if (failureCount >= 2) return false;
  const status = (error as { status?: number } | null)?.status;
  if (typeof status !== 'number') return false;
  return status === 429 || (status >= 500 && status < 600);
};

const DEFAULTS = { staleTime: 60_000, retry: retryOnTransient } as const;

export function usePopularAssetsQuery(
  opts: DeliveryAnalyticsFilters & { metric?: 'requests' | 'bytes' },
  queryOptions?: UseQueryOptions<PopularAssetsResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getPopularAssets(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<PopularAssetsResponse, FetchError>(
    [queryNames.cdnPopularAssets, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useAudienceQuery(
  opts: DeliveryAnalyticsFilters & { dimension?: AudienceDimension },
  queryOptions?: UseQueryOptions<AudienceResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getAudience(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<AudienceResponse, FetchError>(
    [queryNames.cdnAudience, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useTimeseriesQuery(
  opts: DeliveryAnalyticsFilters & { interval?: 'hour' | 'day' },
  queryOptions?: UseQueryOptions<TimeseriesResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getTimeseries(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<TimeseriesResponse, FetchError>(
    [queryNames.cdnTimeseries, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useCachePerformanceQuery(
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<CachePerformanceResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getCachePerformance(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<CachePerformanceResponse, FetchError>(
    [queryNames.cdnCachePerformance, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function usePopularTransformsQuery(
  opts: DeliveryAnalyticsFilters & { groupBy?: TransformGroupBy },
  queryOptions?: UseQueryOptions<PopularTransformsResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getPopularTransforms(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<PopularTransformsResponse, FetchError>(
    [queryNames.cdnPopularTransforms, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useTransformCoverageQuery(
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<TransformCoverageResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getTransformCoverage(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<TransformCoverageResponse, FetchError>(
    [queryNames.cdnTransformCoverage, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useCampaignsQuery(
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<CampaignsResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getCampaigns(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<CampaignsResponse, FetchError>(
    [queryNames.cdnCampaigns, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useCampaignDetailQuery(
  campaignId: string,
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<CampaignDetailResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getCampaignDetail(accessToken, campaignId, opts)),
    [accessToken, campaignId, JSON.stringify(opts)],
  );
  return useQuery<CampaignDetailResponse, FetchError>(
    [queryNames.cdnCampaignDetail, campaignId, opts],
    queryFn,
    { ...DEFAULTS, enabled: Boolean(campaignId), ...queryOptions },
  );
}

export function useChannelsQuery(
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<ChannelsResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getChannels(accessToken, opts)),
    [accessToken, JSON.stringify(opts)],
  );
  return useQuery<ChannelsResponse, FetchError>(
    [queryNames.cdnChannels, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}

export function useChannelDetailQuery(
  channelKey: string,
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<ChannelDetailResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getChannelDetail(accessToken, channelKey, opts)),
    [accessToken, channelKey, JSON.stringify(opts)],
  );
  return useQuery<ChannelDetailResponse, FetchError>(
    [queryNames.cdnChannelDetail, channelKey, opts],
    queryFn,
    { ...DEFAULTS, enabled: Boolean(channelKey), ...queryOptions },
  );
}

export function useDistinctValuesQuery(
  dimension: DistinctDimension,
  opts: DeliveryAnalyticsFilters,
  queryOptions?: UseQueryOptions<DistinctValuesResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getDistinctValues(accessToken, dimension, opts)),
    [accessToken, dimension, JSON.stringify(opts)],
  );
  return useQuery<DistinctValuesResponse, FetchError>(
    [queryNames.cdnDistinctValues, dimension, opts],
    queryFn,
    { ...DEFAULTS, ...queryOptions },
  );
}
